import { pick } from "es-toolkit";

import type { NodeRole } from "@skilitics/nautilus-core";

import type {
  CreateableNode,
  GetManifestByTypeId,
  GetMigrateFromManifestByTypeId,
  GetPlaceholderManifest,
  NodeManifestAny,
  NodeSelector,
  PlatformName,
} from "../types/index.ts";
import type { NodeSelectionCriteria } from "./shared.ts";

export type Filter = (candidate: {
  typeId?: string;
  definitionId?: string;
  role?: string;
  roles?: string[];
}) => boolean;

export type ManifestFilter<TManifest extends NodeManifestAny = NodeManifestAny> = (manifest: TManifest) => boolean;

const impliedPlatforms: PlatformName[] = ["web"]; // can drop this if we make platforms mandatory on manifests

export const createNodeSelectionCriteriaFilter =
  (c: NodeSelectionCriteria): Filter =>
  ({ typeId, definitionId, role, roles }) => {
    const baseFilter = (fc: { typeId: string; definitionId?: string } | { role: string } | { roles: string[] }) => {
      if ("typeId" in fc) {
        if (typeId !== fc.typeId) {
          return false;
        }

        return !("definitionId" in fc && fc.definitionId && definitionId !== fc.definitionId);
      }

      if ("roles" in fc) {
        return fc.roles.some((r) => roles?.includes(r) || role === r);
      }

      return !("role" in fc && fc.role && !(roles?.includes(fc.role) || role === fc.role));
    };

    const baseClause: { typeId: string; definitionId?: string } | { role: string } | { roles: string[] } | undefined =
      c.typeId !== undefined
        ? pick(c, ["typeId", "definitionId"])
        : c.roles !== undefined && c.roles.length > 0
          ? (pick(c, ["roles"]) as { roles: string[] })
          : c.role !== undefined
            ? (pick(c, ["role"]) as { role: string })
            : undefined;

    if (baseClause && !baseFilter(baseClause)) {
      return false;
    }

    if ("include" in c && c.include && !c.include.some(baseFilter)) {
      return false;
    }

    return !("exclude" in c && c.exclude && c.exclude.some(baseFilter));
  };

const createManifestFilter =
  <TManifest extends NodeManifestAny>(c: NodeSelectionCriteria): ManifestFilter<TManifest> =>
  (manifest) => {
    const {
      typeId,
      terminus: { roles, placeholder },
      platforms = impliedPlatforms,
    } = manifest;
    if (placeholder && !c.placeholders) {
      return false;
    }

    // definitionId is a property of a manifest's individual definitions, not the manifest itself,
    // so it is only applied when filtering the extracted creatables (see findDefinitions), not here.
    const { definitionId: _definitionId, ...manifestCriteria } = c;
    return createNodeSelectionCriteriaFilter(manifestCriteria)({ typeId, roles });
  };

const extractCreatables = ({
  typeId,
  title,
  description,
  terminus: { roles },
  designer: { definitions, icon },
}: NodeManifestAny): CreateableNode[] => {
  if (definitions) {
    return Object.entries(definitions)
      .filter(([, definition]) => !definition.internal)
      .map(([definitionId, definition]) => ({
        definitionId,
        typeId,
        title: definition.title,
        description: definition.description,
        roles: definition.role ? [definition.role] : roles,
        icon: definition.icon ?? icon,
      }));
  } else {
    return [
      {
        typeId,
        title,
        description,
        roles,
        icon,
      },
    ];
  }
};

const queryDefinitionsCreate =
  <TManifest extends NodeManifestAny>(manifests: TManifest[]) =>
  (criteria: NodeSelectionCriteria) =>
    manifests.filter(createManifestFilter(criteria));

export type QueryDefinitions = ReturnType<typeof queryDefinitionsCreate>;

const findDefinitionsCreate = (queryDefinitions: QueryDefinitions) => async (criteria: NodeSelectionCriteria) => {
  const definitions = queryDefinitions(criteria).flatMap(extractCreatables);
  return definitions.filter(createNodeSelectionCriteriaFilter(criteria));
};

const getManifestCreate =
  <TManifest extends NodeManifestAny>(manifests: TManifest[]): GetManifestByTypeId =>
  (typeId, { ignoreIfNotFound } = {}) => {
    const manifest = manifests.find((m) => m.typeId === typeId);
    if (!manifest && !ignoreIfNotFound) {
      throw new Error(`Could not find manifest for node type id: ${typeId}.`);
    }
    return manifest as NodeManifestAny;
  };

const getMigrateFromManifestCreate =
  <TManifest extends NodeManifestAny>(manifests: TManifest[]): GetMigrateFromManifestByTypeId =>
  (typeId, opts) => {
    const manifest = manifests.find((m) => m.designer.migratesFromTypeIds?.includes(typeId));
    if (!manifest && !opts?.ignoreIfNotFound) {
      throw new Error(`Could not find designer manifest to migrate to for node type id: ${typeId}.`);
    }
    return manifest as NodeManifestAny;
  };

const getManifestsByRoleCreate =
  <TManifest extends NodeManifestAny>(manifests: TManifest[]) =>
  (role: NodeRole) =>
    manifests.filter((m) => m.terminus.roles?.includes(role));

const getPlaceholderManifestCreate =
  ({
    queryDefinitions,
    getManifest,
  }: {
    queryDefinitions: QueryDefinitions;
    getManifest: GetManifestByTypeId;
  }): GetPlaceholderManifest =>
  (selector: NodeSelector, options) => {
    if ("criteria" in selector && selector.criteria) {
      const manifests = queryDefinitions(selector.criteria);
      if (manifests.length === 0 && !options?.ignoreIfNotFound) {
        throw new Error(`Node with selector: ${JSON.stringify(selector.criteria)} not found.`);
      }
      const manifest = manifests.find((m) => m.terminus.placeholder);
      if (!manifest && !options?.ignoreIfNotFound) {
        throw new Error(`No placeholder for selector: ${JSON.stringify(selector)} found.`);
      }
      return manifest as NodeManifestAny;
    }
    if ("typeId" in selector) {
      const { typeId } = selector;
      const manifest = getManifest(typeId, options);
      // manifest may be undefined if options.ignoreIfNotFound was set
      if (manifest && !manifest.terminus.placeholder) {
        throw new Error(`Node with typeId: ${typeId} not a placeholder.`);
      }
      return manifest;
    }
    throw new Error(`Unexpected placeholder manifest criteria: ${JSON.stringify(selector)}.`);
  };

export const manifestIndexCreate = <TManifest extends NodeManifestAny>(manifests: TManifest[]) => {
  const getManifest = getManifestCreate(manifests);
  const queryDefinitions = queryDefinitionsCreate(manifests);
  return {
    getManifest,
    getManifestsByRole: getManifestsByRoleCreate(manifests),
    getMigrateFromManifest: getMigrateFromManifestCreate(manifests),
    queryDefinitions,
    findDefinitions: findDefinitionsCreate(queryDefinitions),
    getPlaceholderManifest: getPlaceholderManifestCreate({ getManifest, queryDefinitions }),
  };
};

export type ManifestIndex = ReturnType<typeof manifestIndexCreate>;
